import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { submitNewsletter } from "@/lib/api";

const footerLinks = [
  { href: "/", label: "Accueil" },
  { href: "/a-propos", label: "À propos" },
  { href: "/#services", label: "Accompagnements" },
  { href: "/#constellations", label: "Constellations de groupe" },
  { href: "/#faq", label: "FAQ" },
  { href: "/#cta", label: "Contact" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Merci de renseigner votre email.");
      return;
    }
    setLoading(true);
    try {
      await submitNewsletter({ email: email.trim() });
      toast.success("Inscription enregistrée. Merci !");
      setEmail("");
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1E1E2A] text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <h3 className="font-serif-display text-3xl font-light">Francine Banzadio</h3>
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-[#C98FA3]">
              Psychotraumas · Schémas émotionnels
            </p>
            <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-sm">
              Accompagnement individuel et constellations familiales en groupe.
              En présentiel et à distance.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C98FA3] font-medium mb-5">
              Navigation
            </p>
            <ul className="space-y-3">
              {footerLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                    className="text-sm text-white/80 hover:text-[#E6D6DF] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[#C98FA3] font-medium mb-5">
              Contact
            </p>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#E6D6DF]" />
                <a
                  href="mailto:contact.constellharmonie@gmail.com"
                  data-testid="footer-email"
                  className="hover:text-[#E6D6DF] transition-colors"
                >
                  contact.constellharmonie@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#E6D6DF]" />
                <a
                  href="tel:+33664501496"
                  data-testid="footer-phone"
                  className="hover:text-[#E6D6DF] transition-colors"
                >
                  +33 6 64 50 14 96
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#E6D6DF]" />
                <span data-testid="footer-address">
                  La Motte-Servolex (73)
                  <br />
                  &amp; Artemare (01)
                </span>
              </li>
            </ul>

            <form
              onSubmit={subscribe}
              data-testid="newsletter-form"
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email pour la newsletter"
                data-testid="newsletter-input"
                className="bg-white/8 border-white/15 text-white placeholder:text-white/40 rounded-full h-12 px-5"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="bg-[#C98FA3] text-white hover:bg-[#E6D6DF] hover:text-[#3A2E6E] disabled:opacity-60 transition-all duration-300 rounded-full px-6 h-12 font-medium text-sm"
              >
                {loading ? "…" : "S'inscrire"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <p>© {year} Francine Banzadio. Tous droits réservés.</p>
          <p>Site élaboré avec soin · Confidentialité respectée</p>
        </div>
      </div>
    </footer>
  );
}
